import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  ApplicationStatusResponse,
  CreateProcessResponse,
  ProcessingRequest,
  QuestionnaireUploadFilePreSignedUrlRequest,
  QuestionnaireUploadFilePreSignedUrlResponse,
} from '@codegen';
import { DefaultService } from '@codegen/service';
import { StatusCacheService } from '@modules/shared/services/status-cache.service';
import {
  SetButtons,
  SetCurrentStep,
  SetFormField,
  SetFormSubtitle,
  SetFormTitle,
  SetInvalidField,
  SetProcessId,
} from '@modules/dynamic-form/store/dynamicForm.action';
import { ErrorsService } from '@modules/errors/services/errors.service';
import { ProposalRoutingConstants } from '@modules/order/order.const';
import { AgreementsRoutingConstants } from '@modules/agreements/constants/agreementsRouting.constants';
import { ProposalStatus } from '@modules/order/order.enum';
import { SetIsNeedConfirmation } from '@modules/order/store/proposal/proposal.action';
import { ScoringRoutingConstants } from '@modules/scoring/constants/scoringRouting.constants';

import {
  GetRemoteUploadedQuestionnaireFilesObjectId,
  GetScoring,
  RemoteUploadQuestionnaireFile,
  ScoringTypes,
  SendQuestionnaire,
  StartScoring,
} from '@modules/scoring/store/scoring.action';
import { CommonApiConstants } from '@modules/shared/constants/commonApi.constants';
import { AppState } from '@modules/shared/store';
import {
  GetConfirmCode,
  HideSpinnerLoading,
  SetBackLinkFromDetails,
  SetSmsResponseCode,
  SetValidationsText,
  UpdateQuestionnaireProcess,
} from '@modules/shared/store/common.actions';
import { fileToMd5 } from '@modules/shared/utils';
import { StatusRoutingConstants } from '@modules/status/constants/statusRouting.constants';
import { SetProposalStatus } from '@modules/status/store/status.action';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { concatMap, filter, map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable()
export class ScoringEffects {
  private readonly actions$ = inject(Actions);
  private readonly defaultService = inject(DefaultService);
  private readonly statusCacheService = inject(StatusCacheService);
  private readonly router = inject(Router);
  private readonly store = inject<Store<AppState>>(Store);
  public StartScoring$ = createEffect(() =>
    this.actions$.pipe(
      ofType<StartScoring>(ScoringTypes.StartScoring),
      map(data => data.payload),
      switchMap(data =>
        this.statusCacheService.getStatus(data.id).pipe(
          switchMap(
            (
              response: ApplicationStatusResponse
            ): Observable<CreateProcessResponse> | Action[] => {
              this.store.dispatch(
                new SetProposalStatus({
                  proposalStatus: response.applicationStatus,
                  proposalStatusDescription:
                    response.applicationStatusDescription,
                  proposalStatusTitle: response.applicationStatusTitle,
                  proposalStatusIcon: response.applicationStatusIcon,
                  waitingTime: response.waitingTime,
                })
              );
              switch (response.applicationStatus.key) {
                case ProposalStatus.preScoringQuestionnaireNeed:
                case ProposalStatus.postScoringQuestionnaireNeed:
                  return this.defaultService.questionnaireCreateProcess({
                    questionnaireId: data.questionnaireId,
                    params: { applicationId: data.id },
                  });
                case ProposalStatus.agreementNeed:
                  this.store.dispatch(new HideSpinnerLoading());
                  this.router.navigateByUrl(
                    `/agreements/${AgreementsRoutingConstants.proposalAgreement}/${data.id}`
                  );
                  return [];
                case ProposalStatus.scoring:
                case ProposalStatus.underwriting:
                  if (response.waitingTime) {
                    this.router.navigate([
                      `/status/${StatusRoutingConstants.timer}/${data.id}`,
                    ]);
                    return [new HideSpinnerLoading()];
                  }
                  return [];
                default:
                  this.store.dispatch(
                    new SetBackLinkFromDetails(
                      `${'/order/' + ProposalRoutingConstants.proposalsList}`
                    )
                  );
                  this.router.navigateByUrl(
                    `${'/order/' + ProposalRoutingConstants.proposalsList}`
                  );
                  return [];
              }
            }
          )
        )
      ),
      filter((response: CreateProcessResponse) => !!response.processId),
      switchMap((response: CreateProcessResponse) => {
        return [
          new SetProcessId(response.processId),
          new GetScoring(response.processId),
        ];
      })
    )
  );
  public GetScoring$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetScoring>(ScoringTypes.GetScoring),
      map(data => data.payload),
      switchMap((processId: string) =>
        this.defaultService.questionnaireProcess({ processId })
      ),
      map(response => {
        this.store.dispatch(
          new SetFormField(response.questionnaireStep?.groupList)
        );
        this.store.dispatch(
          new SetFormTitle(
            response.questionnaireStep?.groupList?.[0]?.displayName
          )
        );
        this.store.dispatch(
          new SetFormSubtitle(response.questionnaireStep?.groupList?.[0]?.hint)
        );
        this.store.dispatch(
          new SetCurrentStep(response.questionnaireStep?.hint)
        );
        this.store.dispatch(new SetButtons(response.allowedActions));
        this.store.dispatch(
          new SetIsNeedConfirmation(response.needConfirmation)
        );
      }),
      switchMap(() => [new HideSpinnerLoading()])
    )
  );
  private readonly dialogRef = inject(MatDialog);
  private readonly http = inject(HttpClient);
  private readonly errorsService = inject(ErrorsService);
  public RemoteUploadQuestionnaireFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RemoteUploadQuestionnaireFile>(
        ScoringTypes.RemoteUploadQuestionnaireFile
      ),
      map(data => data.payload),
      switchMap((formData: FormData) => {
        const file = formData.get('file') as File;

        return fileToMd5(file).pipe(
          switchMap(md5 => {
            const processId = formData.get('processId') as string;
            const questionId = formData.get('questionId') as string;

            const fileInfo: QuestionnaireUploadFilePreSignedUrlRequest = {
              checkSum: md5,
              contentType: (file.type as string) || 'application/pdf',
              fileName: file.name as string,
              processId,
              questionId,
              size: file.size,
            };

            return this.defaultService
              .questionnaireFilePreSignedUrl(fileInfo)
              .pipe(
                concatMap(
                  (response: QuestionnaireUploadFilePreSignedUrlResponse) => {
                    if (response.errorInfo.error === '11003') {
                      this.errorsService.showError(response, '', {
                        actionId: response.actionId || '',
                        questionnaireId: response.questionnaireId || '',
                      });

                      return [
                        new UpdateQuestionnaireProcess({
                          questionnaireId: 'SCORING',
                        }),
                      ];
                    }

                    const url = response.preSignedUrl;
                    const finalUrl = environment.production
                      ? url
                      : '/uploadProxy' +
                        url.replace('https://s3-minsk-dc2.cloud.mts.by', '');

                    return this.http.put(finalUrl, file).pipe(
                      concatMap(() => [
                        new GetRemoteUploadedQuestionnaireFilesObjectId({
                          [questionId]: response.objectId,
                        }),
                      ])
                    );
                  }
                )
              );
          })
        );
      })
    )
  );

  public SendQuestionnaire$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SendQuestionnaire>(ScoringTypes.SendQuestionnaire),
      map(data => data.payload),
      switchMap(data => {
        const { processId, stepType, formValue, smsCode, applicationId } = data;
        return this.defaultService
          .questionnaireProcess({
            processId: processId,
            action: stepType as ProcessingRequest.ActionEnum,
            questionMap: formValue,
            confirmationData: smsCode,
          })
          .pipe(
            switchMap(res => {
              if (
                res.errorInfo.error === '10103' ||
                res.errorInfo.error === '10104'
              ) {
                return [
                  new SetSmsResponseCode(res.errorInfo.error),
                  new SetValidationsText(res.errorInfo.errorText),
                ];
              } else if (res.errorInfo.error === '11003') {
                this.errorsService.showError(res, '', {
                  actionId: res.actionId || '',
                  questionnaireId: res.questionnaireId || '',
                });
                localStorage.removeItem('processId');
                localStorage.setItem('applicationId', applicationId);
                this.dialogRef.closeAll();

                return [
                  new UpdateQuestionnaireProcess({
                    questionnaireId: 'SCORING',
                    applicationId,
                  }),
                ];
              } else if (res.errorInfo.error === '10102') {
                return [
                  new GetConfirmCode({
                    serviceToConfirm: CommonApiConstants.questionnaireProcess,
                    resend: false,
                  }),
                ];
              } else if (res.errorInfo.error === '11002') {
                this.dialogRef.closeAll();
                this.router.navigateByUrl(
                  `/scoring/${ScoringRoutingConstants.questionnaire}/${applicationId}`
                );
                return [
                  new SetFormField(res.questionnaireStep.groupList),
                  new SetFormTitle(
                    res.questionnaireStep.groupList[0].displayName
                  ),
                  new SetFormSubtitle(res.questionnaireStep.groupList[0].hint),
                  new SetCurrentStep(res.questionnaireStep.hint),
                  new SetButtons(res.allowedActions),
                  new SetIsNeedConfirmation(res.needConfirmation),
                  new SetInvalidField(res.malformedQuestions),
                  new HideSpinnerLoading(),
                ];
              } else {
                this.dialogRef.closeAll();
                this.router.navigateByUrl(
                  `/status/${StatusRoutingConstants.timer}/${applicationId}`
                );
                return [new HideSpinnerLoading()];
              }
            })
          );
      })
    )
  );
}
